import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { fadeInExpandOnEnterAnimation, fadeOutCollapseOnLeaveAnimation } from 'angular-animations';
import { interval, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { NotificationInfo } from '../../interfaces/notifications.interfaces';
import { NotificationService } from '../../services/notification.service';

const NOTIFICATION_TEXT_FADE_OUT_TIME_MS: number = 1000;
const NOTIFICATION_TEXT_FADE_IN_TIME_MS: number = 500;
const NOTIFICATION_TEXT_STATIC_TIME_MS: number = 4000;

const NOTIFICATION_DISPLAY_PERIOD_MS: number = NOTIFICATION_TEXT_STATIC_TIME_MS + NOTIFICATION_TEXT_FADE_OUT_TIME_MS + NOTIFICATION_TEXT_FADE_IN_TIME_MS;

@Component({
  selector: 'app-notifier',
  templateUrl: './notifier.component.html',
  styleUrls: ['./notifier.component.scss'],  
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    fadeInExpandOnEnterAnimation(),
    fadeOutCollapseOnLeaveAnimation(),
    trigger('displayHide', [
      state('display', style({
        opacity: 1,
      })),
      state('hide', style({
        opacity: 0,
      })),
      transition('display => hide', [
        animate(`${NOTIFICATION_TEXT_FADE_OUT_TIME_MS}ms`)
      ]),
      transition('hide => display', [
        animate(`${NOTIFICATION_TEXT_FADE_IN_TIME_MS}ms`)
      ]),
    ]),
  ]
})
export class NotifierComponent implements OnInit {
  public isTransitioning: boolean = false;
  public isNotificationActive: boolean = false;
  private displayIndex: number = 0;
  private notifications: NotificationInfo[] = [];
  private displayNotification: NotificationInfo = null;

  constructor(
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private translate: TranslateService,
  ) { }

  public ngOnInit(): void {
    interval(NOTIFICATION_DISPLAY_PERIOD_MS).subscribe((elapsedPeriods: number) => {
      this.onDisplayIntervalElapsed();
    })

    this.notificationService.onActiveNotificationsChangedCallback.subscribe((data: NotificationInfo[]) => {
      this.onNotificationsChanged(data);
    });

    this.onNotificationsChanged(this.notificationService.getActiveNotifications());
  }

  public isNotificationClickable(): boolean {
    const activeNotification: NotificationInfo = this.getActiveNotificationInfo();

    return Boolean(activeNotification?.destinationPage);
  }

  public onNotificationClick(): void {
    const activeNotificationInfo: NotificationInfo = this.getActiveNotificationInfo();

    if (activeNotificationInfo?.destinationPage) {
      let params: object = {};

      if (activeNotificationInfo.destinationTab) {
        params = { tab: activeNotificationInfo.destinationTab };
      };

      this.router.navigate([`${activeNotificationInfo!.destinationPage}`, params ]);
    }
  }

  public getTranslation(): string {
    const key: string = `NotifierComponent.${this.displayNotification.type}`;
    return this.translate.instant(key, this.displayNotification.params);
  }

  private onNotificationsChanged(notifications: NotificationInfo[]): void {
    this.notifications = [...notifications];
    if (!this.isNotificationActive) {
      // Activate immediatly
      this.displayIndex = 0;
      this.updateDisplayNotification();
    }
    else if (!this.notifications.some((notification: NotificationInfo) => notification.type === this.displayNotification.type)) {
      // No notification of this type is active, transition it away
      this.onDisplayIntervalElapsed(true);
    }
    this.isNotificationActive = this.notifications.length > 0;
    this.cdr.markForCheck();
  }

  private getActiveNotificationInfo(): NotificationInfo {
    return this.displayNotification;
  }

  private updateDisplayNotification(): void {
    if (this.notifications.length <= 0) {
      this.displayIndex = 0;
      this.displayNotification = null;
      return;
    }

    this.displayIndex = (this.displayIndex + 1) % this.notifications.length;

    this.displayNotification = this.notifications[this.displayIndex];
  }

  private onDisplayIntervalElapsed(forceTransition: boolean = false): void {
    const nrOfActiveNotifications: number = this.notificationService.getActiveNotifications().length;

    // No need to fade in / fade out when none or only one notification is active. 
    if (nrOfActiveNotifications < 2 && !forceTransition) {
      this.updateDisplayNotification();
      return;
    }

    this.isTransitioning = true;

    timer(NOTIFICATION_TEXT_FADE_OUT_TIME_MS)
    .pipe(take(1))
    .subscribe(() => {
      this.updateDisplayNotification();
      this.isTransitioning = false;
      this.cdr.markForCheck();
    });
    this.cdr.markForCheck();
  }
}
